import React, { useState } from 'react'
import { Box, Typography } from '@mui/material'
import {
  CommonTextField,
  CommonButton,
  CommonPhone,
  CommonDatePicker,
} from '@/components/common'
import { useRequestMagicLink, usePortalConstantValue } from '@/hooks'
import { toUTCDateString, extractValidationErrors } from '@/utils'
import { useTenantContext } from '@/context'
import { localStorageService } from '@/services'
import { STORAGE_KEYS, accountRecoverySchema } from '@/constants'

const steps = {
  ENTER_CONTACT: 1,
  DONOR_NOT_FOUND: 3,
  MULTIPLE_DONORS_FOUND: 4,
  NEW_EMAIL_REQUIRED: 5,
  MAGIC_LINK_SENT: 6,
}

export const AccountRecovery = ({ redirectUrl, onSuccess }) => {
  const { tenant } = useTenantContext()
  const { loadingRequestMagicLink, requestMagicLink } = useRequestMagicLink()
  const getPortalConstantValue = usePortalConstantValue()

  const useMagicLinkSms =
    tenant?.configuration_options?.find(
      item => item.code === 'USE_MAGIC_LINK_SMS'
    )?.value === 'true'

  const [step, setStep] = useState(steps.ENTER_CONTACT)
  const [errors, setErrors] = useState({})
  const [formData, setFormData] = useState({
    email: '',
    phoneNumber: '',
    birthdate: '',
    donorNumber: '',
    newEmail: '',
  })
  const [targetContact, setTargetContact] = useState('')
  const [isUnknownError, setIsUnknownError] = useState(false)

  const handleAccountRecovery = async e => {
    if (e) e.preventDefault()
    setErrors({})
    setTargetContact('')
    setIsUnknownError(false)
    try {
      const payload = {
        ...formData,
        redirectUrl,
        birthdate: formData.birthdate
          ? toUTCDateString(formData.birthdate)
          : undefined,
      }

      await accountRecoverySchema.validate(payload, { abortEarly: false })
      const response = await requestMagicLink(payload)
      const { code, data } = getStepData(response?.response)
      if (data) setTargetContact(data)
      if (code) setStep(code)
      resetForm()
    } catch (error) {
      const fieldErrors = extractValidationErrors(error)
      if (fieldErrors) {
        setErrors(fieldErrors)
        return
      } else if (error?.response?.status === 400) {
        const raw = error?.response?.data?.response
        const { code, data } = getStepData(raw)
        if (data) setTargetContact(data)
        if (code) setStep(code)
        else setIsUnknownError(true)
      } else setIsUnknownError(true)
    }
  }

  const getStepData = raw => {
    if (typeof raw === 'string') {
      try {
        const [codeKey, dataStr] = raw.split(':', 2)
        const nextStep = steps[codeKey]
        if (nextStep) {
          return { code: nextStep, data: dataStr }
        }
      } catch (error) {
        console.log('error', error)
        return null
      }
    }
  }

  const resetForm = () => {
    setFormData({
      email: '',
      phoneNumber: '',
      birthdate: '',
      donorNumber: '',
      newEmail: '',
    })
    setErrors({})
    setIsUnknownError(false)
  }

  const handleContinueAsGuest = () => {
    localStorageService.setItem(STORAGE_KEYS.IS_GUEST, 'true')
    localStorageService.setItem(STORAGE_KEYS.IS_GUEST_AT, Date.now().toString())
    onSuccess()
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component='form'
        onSubmit={handleAccountRecovery}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: 700,
          gap: 3,
        }}
      >
        <Typography
          variant='h4'
          fontWeight={500}
          sx={{
            textAlign: 'center',
            mb: 2,
          }}
        >
          {step === steps.ENTER_CONTACT && (
            <>
              Enter your contact information, and we'll send a link to login
              <br></br>
              <Typography
                variant='body1'
                fontWeight={500}
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                {useMagicLinkSms
                  ? 'Search by either email or phone number'
                  : 'Search by email'}
              </Typography>
            </>
          )}

          {step === steps.DONOR_NOT_FOUND && (
            <>
              We did not find a donor record with that contact information
              <br></br>
              <Typography
                variant='body1'
                fontWeight={500}
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                {useMagicLinkSms
                  ? 'Enter another email or phone to try again'
                  : 'Enter another email to try again'}
              </Typography>
            </>
          )}
          {step === steps.MULTIPLE_DONORS_FOUND && (
            <>
              Almost there! We need a little more information
              <br></br>
              <Typography
                variant='body1'
                fontWeight={500}
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                Enter either donor number or birthdate
                <br></br>
                Need Help?
                <CommonButton
                  id='need-help-button'
                  label={tenant?.tenant_info?.phone_number}
                  link
                  to={`tel:${tenant?.tenant_info?.phone_number}`}
                  variant='text'
                  size='small'
                  style={{ fontSize: '0.875rem', fontWeight: 400 }}
                />
              </Typography>
            </>
          )}
          {step === steps.NEW_EMAIL_REQUIRED && (
            <>
              That email is already registered
              <br></br>
              <Typography
                variant='body1'
                fontWeight={500}
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                Please enter a different email
              </Typography>
            </>
          )}
          {step === steps.MAGIC_LINK_SENT && (
            <>
              Magic Link is on the way!
              <br></br>
              <Typography
                variant='body1'
                fontWeight={500}
                sx={{
                  textAlign: 'center',
                  color: 'text.secondary',
                }}
              >
                Your magic link has been sent to your contact information
              </Typography>
            </>
          )}
        </Typography>

        {isUnknownError && (
          <Typography
            variant='h6'
            fontWeight={500}
            sx={{ textAlign: 'center', my: 2 }}
          >
            An unknown error occurred, please try again later.
            <br></br>
            If you have questions please call{' '}
            <a href={`tel:${tenant?.tenant_info?.phone_number}`}>
              {tenant?.tenant_info?.phone_number}
            </a>
          </Typography>
        )}

        {(step === steps.ENTER_CONTACT ||
          step === steps.DONOR_NOT_FOUND ||
          step === steps.MAGIC_LINK_SENT) && (
          <>
            <CommonTextField
              id='email'
              label='Email'
              value={formData.email}
              setValue={value => setFormData({ ...formData, email: value })}
              type='email'
              error={errors.email}
              disabled={!!formData.phoneNumber}
              placeholder={getPortalConstantValue('LOGIN_EMAIL_PLACEHOLDER')}
              inputProps={{
                name: 'email',
                autoComplete: 'username email',
              }}
            />

            {useMagicLinkSms && (
              <CommonPhone
                id='phone-number'
                label='Phone Number'
                value={formData.phoneNumber}
                setValue={value =>
                  setFormData({ ...formData, phoneNumber: value })
                }
                mask='(999) 999-9999'
                error={errors.phoneNumber}
                disabled={!!formData.email}
                placeholder='(###) ###-####'
                inputProps={{
                  name: 'phoneNumber',
                }}
              />
            )}
          </>
        )}

        {step === steps.MULTIPLE_DONORS_FOUND && (
          <>
            <CommonTextField
              id='donor-number'
              label='Donor Number'
              value={formData.donorNumber}
              setValue={value =>
                setFormData({ ...formData, donorNumber: value })
              }
              disabled={!!formData.birthdate}
              placeholder='Enter your donor number'
              inputProps={{
                name: 'donorNumber',
              }}
            />
            <CommonDatePicker
              id='birthdate'
              label='Birthdate'
              value={formData.birthdate}
              disabled={!!formData.donorNumber}
              onChange={value => setFormData({ ...formData, birthdate: value })}
              placeholder='Enter your birthdate'
            />
          </>
        )}
        {step === steps.NEW_EMAIL_REQUIRED && (
          <CommonTextField
            id='new-email'
            label='New Email'
            value={formData.newEmail}
            setValue={value => setFormData({ ...formData, newEmail: value })}
            type='email'
            error={errors.newEmail}
            placeholder='Enter your new email'
            inputProps={{
              name: 'email',
              autoComplete: 'username email',
            }}
          />
        )}
        {(step === steps.ENTER_CONTACT ||
          step === steps.DONOR_NOT_FOUND ||
          step === steps.MAGIC_LINK_SENT ||
          step === steps.MULTIPLE_DONORS_FOUND ||
          step === steps.NEW_EMAIL_REQUIRED) && (
          <CommonButton
            id='send-link-button'
            label={
              step === steps.DONOR_NOT_FOUND
                ? 'Try Again'
                : step === steps.MAGIC_LINK_SENT
                  ? 'Resend Link'
                  : 'Send Link'
            }
            type='submit'
            onClick={handleAccountRecovery}
            loading={loadingRequestMagicLink}
            size='large'
            style={{ width: '100%' }}
          />
        )}

        {step !== steps.ENTER_CONTACT && (
          <CommonButton
            id='continue-as-guest-button'
            label='Continue as Guest'
            link
            variant='text'
            onClick={handleContinueAsGuest}
            size='small'
            sx={{ fontWeight: 400, mt: 2 }}
          />
        )}
      </Box>
    </Box>
  )
}
